import { inject, Injectable } from '@angular/core';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { type DownloadWorkflowNode } from '@blaze-shared/workflow-domain';

import { EngineFileDownloads } from '../../../engine-ports/public-api';
import { normalizeNodeInput } from '../../node-input-rows';

/**
 * Downloads the files the node's URL expression resolves to, once per input row.
 *
 * A multi-value field resolves to several URLs at once, so each is downloaded (BL-7435). A node
 * that zips its files downloads the archive the row carries instead of the expression's URL.
 */
@Injectable({ providedIn: 'root' })
export class DownloadActionService {
  private readonly engineFileDownloads = inject(EngineFileDownloads);
  private readonly iss = inject(IntelliSenseService);

  download(node: DownloadWorkflowNode, nodeInput: any, ctx: RuntimeContext): void {
    for (const row of normalizeNodeInput(nodeInput)) {
      let context = this.iss.updateRuntimeContext(ctx, row);
      context = this.iss.updateRuntimeContextWithStoredVarsDefaults(context, node.nodeType);
      const url = this.iss.eval(node.url, context) as string | string[] | undefined;

      if (node.createZipArchive && !!row?.url) {
        this.engineFileDownloads.downloadSecureFile(row.url);
      } else if (!node.createZipArchive && url && typeof url === 'string') {
        this.engineFileDownloads.downloadSecureFile(url);
      } else if (Array.isArray(url)) {
        url.forEach((urlItem) => this.engineFileDownloads.downloadSecureFile(urlItem));
      }
    }
  }
}
